import { useAuthStore } from '../stores/auth';

export class SinchVideoService {
  constructor() {
    this.client = null;
    this.currentCall = null;
    this.currentRoom = null;
  }

  // Validate dynamic initialization
  async validateAndInitialize() {
    const authStore = useAuthStore();

    if (!authStore.user) {
      throw new Error("User not logged in. Ensure authentication before initializing Sinch.");
    }

    if (!authStore.sinchClient) {
      throw new Error("Sinch client is not initialized. Call `authStore.login` to set up Sinch.");
    }

    this.client = authStore.sinchClient;
  }

  // Initialize video service
  async initialize() {
    if (!this.client) {
      await this.validateAndInitialize();
    }
    return true;
  }

  // Start video call
  async startCall(participants, options = {}) {
    try {
      if (!this.client) {
        await this.initialize();
      }

      this.currentCall = await this.client.callUser({
        userId: participants[0],
        video: options.video !== false,
        audio: options.audio !== false,
      });

      return this.currentCall;
    } catch (error) {
      console.error('Error starting call:', error);
      throw error;
    }
  }

  // Create conference room
  async createRoom(roomConfig) {
    try {
      if (!this.client) {
        await this.initialize();
      }

      this.currentRoom = await this.client.createRoom({
        name: roomConfig.name,
        maxParticipants: roomConfig.maxParticipants,
        moderators: roomConfig.moderators,
        audioEnabled: roomConfig.audioEnabled,
        videoEnabled: roomConfig.videoEnabled,
        recordingEnabled: roomConfig.recordingEnabled,
      });

      return this.currentRoom;
    } catch (error) {
      console.error('Error creating room:', error);
      throw error;
    }
  }

  // Join conference room
  async joinRoom(roomId) {
    try {
      if (!this.client) {
        await this.initialize();
      }

      this.currentRoom = await this.client.joinRoom(roomId);
      return this.currentRoom;
    } catch (error) {
      console.error('Error joining room:', error);
      throw error;
    }
  }

  // Leave room
  async leaveRoom() {
    try {
      if (this.currentRoom) {
        await this.currentRoom.leave();
        this.currentRoom = null;
      }
    } catch (error) {
      console.error('Error leaving room:', error);
      throw error;
    }
  }

  // Toggle audio
  async toggleAudio(enabled) {
    try {
      if (this.currentCall) {
        await this.currentCall.mute(!enabled);
      }
      if (this.currentRoom) {
        await this.currentRoom.mute(!enabled);
      }
    } catch (error) {
      console.error('Error toggling audio:', error);
      throw error;
    }
  }

  // Toggle video
  async toggleVideo(enabled) {
    try {
      if (this.currentCall) {
        await this.currentCall.pauseVideo(!enabled);
      }
      if (this.currentRoom) {
        await this.currentRoom.pauseVideo(!enabled);
      }
    } catch (error) {
      console.error('Error toggling video:', error);
      throw error;
    }
  }

  // Start screen sharing
  async startScreenShare() {
    try {
      if (this.currentCall) {
        await this.currentCall.startScreenShare();
      }
      if (this.currentRoom) {
        await this.currentRoom.startScreenShare();
      }
    } catch (error) {
      console.error('Error starting screen share:', error);
      throw error;
    }
  }

  // Stop screen sharing
  async stopScreenShare() {
    try {
      if (this.currentCall) {
        await this.currentCall.stopScreenShare();
      }
      if (this.currentRoom) {
        await this.currentRoom.stopScreenShare();
      }
    } catch (error) {
      console.error('Error stopping screen share:', error);
      throw error;
    }
  }

  // End call/conference
  async end() {
    try {
      if (this.currentCall) {
        await this.currentCall.hangup();
        this.currentCall = null;
      }
      if (this.currentRoom) {
        await this.leaveRoom();
      }
    } catch (error) {
      console.error('Error ending call/conference:', error);
      throw error;
    }
  }

  // Add event listeners
  on(event, handler) {
    if (!this.client) {
      throw new Error("Sinch client is not initialized. Call `initialize()` first.");
    }
    this.client.on(event, handler);
  }

  // Remove event listeners
  off(event, handler) {
    if (!this.client) {
      throw new Error("Sinch client is not initialized. Call `initialize()` first.");
    }
    this.client.off(event, handler);
  }

  // Clean up
  cleanup() {
    this.end();
    if (this.client) {
      this.client.dispose();
    }
    this.client = null;
  }
}